if (!customElements.get('deal-of-day-section')) {
  class DealOfDaySection extends HTMLElement {
    connectedCallback() {
      this._initCountdown();
      this._initCarousel();
      this._initWishlist();
      this._initCartButtons();
    }

    disconnectedCallback() {
      clearInterval(this._timer);
    }

    /* ── Countdown ── */
    _initCountdown() {
      const el = this.querySelector('.dotd-countdown');
      if (!el) return;
      const endRaw = el.dataset.end;
      if (!endRaw) return;
      let end = new Date(endRaw).getTime();
      if (isNaN(end)) return;

      // "Automatically rerun deal" — instead of a fixed one-time end date,
      // treat `end` as the FIRST cycle's end and keep rolling it forward by
      // one cycle length for as long as it's already in the past, so the
      // countdown resumes mid-cycle rather than needing the merchant to
      // manually update the date every day/week. Only the timer resets —
      // the product blocks themselves are untouched.
      const repeat = el.dataset.repeat;
      const cycleMs = repeat === 'daily' ? 86400000 : repeat === 'weekly' ? 604800000 : 0;
      if (cycleMs > 0) {
        const now = Date.now();
        if (end <= now) {
          const cyclesElapsed = Math.ceil((now - end) / cycleMs);
          end += cyclesElapsed * cycleMs;
        }
      }

      const h = el.querySelector('[data-unit="hrs"]');
      const m = el.querySelector('[data-unit="mins"]');
      const s = el.querySelector('[data-unit="secs"]');

      const tick = () => {
        let diff = end - Date.now();
        if (diff <= 0) {
          if (cycleMs > 0) {
            end += cycleMs;
            diff = end - Date.now();
          } else {
            h.textContent = '00'; m.textContent = '00'; s.textContent = '00';
            clearInterval(this._timer);
            return;
          }
        }
        const hrs  = Math.floor(diff / 3600000);
        const mins = Math.floor((diff % 3600000) / 60000);
        const secs = Math.floor((diff % 60000) / 1000);
        h.textContent = String(hrs).padStart(2, '0');
        m.textContent = String(mins).padStart(2, '0');
        s.textContent = String(secs).padStart(2, '0');
      };

      tick();
      this._timer = setInterval(tick, 1000);
    }

    /* ── Carousel ── */
    _initCarousel() {
      this._track   = this.querySelector('.dotd-track');
      this._prevBtn = this.querySelector('.dotd-prev');
      this._nextBtn = this.querySelector('.dotd-next');
      if (!this._track) return;

      const cardWidth = () => {
        const card = this._track.querySelector('.dotd-card');
        if (!card) return 210;
        const gap = parseFloat(getComputedStyle(this._track).gap) || 10;
        return card.offsetWidth + gap;
      };

      if (this._prevBtn) this._prevBtn.addEventListener('click', () => {
        this._track.scrollBy({ left: -cardWidth(), behavior: 'smooth' });
      });

      if (this._nextBtn) this._nextBtn.addEventListener('click', () => {
        this._track.scrollBy({ left: cardWidth(), behavior: 'smooth' });
      });

      this._track.addEventListener('scroll', () => this._updateNav(), { passive: true });
      // Update after images load / layout settles
      setTimeout(() => this._updateNav(), 100);
    }

    _updateNav() {
      const t = this._track;
      if (!t) return;
      const atStart = t.scrollLeft <= 2;
      const atEnd   = t.scrollLeft >= t.scrollWidth - t.clientWidth - 4;
      // aria-hidden alone doesn't remove a button from the keyboard tab
      // order, so a hidden-but-still-focusable nav button is a real a11y
      // bug (Lighthouse: "aria-hidden elements contain focusable
      // descendants") — tabindex has to be toggled alongside it.
      if (this._prevBtn) {
        this._prevBtn.setAttribute('aria-hidden', atStart ? 'true' : 'false');
        this._prevBtn.tabIndex = atStart ? -1 : 0;
      }
      if (this._nextBtn) {
        this._nextBtn.setAttribute('aria-hidden', atEnd ? 'true' : 'false');
        this._nextBtn.tabIndex = atEnd ? -1 : 0;
      }
    }

    /* ── Wishlist — wired to saved-items localStorage ── */
    _initWishlist() {
      const SAVE_KEY = 'laki:saved-products';

      const readSaved = () => {
        try {
          const raw = localStorage.getItem(SAVE_KEY);
          const list = raw ? JSON.parse(raw) : [];
          return Array.isArray(list) ? list : [];
        } catch { return []; }
      };

      const writeSaved = (list) => {
        try { localStorage.setItem(SAVE_KEY, JSON.stringify(list)); } catch {}
      };

      const getId = (item) => String(typeof item === 'string' ? item : item.id || '');

      // Sync initial state from localStorage
      const savedIds = new Set(readSaved().map(getId));
      this.querySelectorAll('.dotd-card__wish').forEach(btn => {
        if (btn.dataset.productId && savedIds.has(btn.dataset.productId)) {
          btn.classList.add('is-wished');
        }

        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const id = btn.dataset.productId;
          if (!id) { btn.classList.toggle('is-wished'); return; }

          let list = readSaved();
          const alreadySaved = list.some(item => getId(item) === id);

          if (alreadySaved) {
            list = list.filter(item => getId(item) !== id);
            btn.classList.remove('is-wished');
          } else {
            list.push({
              id,
              title:        btn.dataset.productTitle || '',
              price:        btn.dataset.productPrice || '',
              comparePrice: btn.dataset.productCompare || '',
              image:        btn.dataset.productImage || '',
              url:          btn.dataset.productUrl || '',
              variantId:    btn.dataset.productVariant || ''
            });
            btn.classList.add('is-wished');
          }

          writeSaved(list);
          window.dispatchEvent(new CustomEvent('laki:saved-updated'));
        });
      });
    }

    /* ── Cart icon — add to cart ── */
    _initCartButtons() {
      this.querySelectorAll('.dotd-card__cart-btn').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          e.stopPropagation();

          const variantId = btn.dataset.variantId;
          if (!variantId || btn.classList.contains('is-adding')) return;

          btn.classList.add('is-adding');

          try {
            const addRes = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
            });

            if (!addRes.ok) throw new Error('ATC failed');

            // Refresh drawer HTML + badge, then open the drawer
            const cart = await (window.theme?.refreshCartDrawer?.({ open: true })
              ?? fetch('/cart.js').then(r => r.json()));

            if (window.LakiEvents?.emit) {
              window.LakiEvents.emit(window.LakiEvents.EVENTS.CART_ADD, {
                count: cart?.item_count
              });
            }

            btn.classList.remove('is-adding');
            btn.classList.add('is-added');
            setTimeout(() => btn.classList.remove('is-added'), 2000);
          } catch {
            btn.classList.remove('is-adding');
          }
        });
      });
    }
  }

  customElements.define('deal-of-day-section', DealOfDaySection);
}
